import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

import { fetchRest } from '@/lib/hook/tanstackQuery'
import { ApiQueryEndpoint } from '@/src/ApiQueryEndpoint'

const CONFIRMATION_ATTEMPTS = 10
const CONFIRMATION_INTERVAL_MS = 1000
const POLL_INTERVAL_MS = 60000

// Reproduit le resolver isInMaintenance (apollo/server/resolvers/Maintenance/isInMaintenance.ts) :
// il appelait getSocietyInfo (GET storefront) et renvoyait false si l'appel reussit,
// true en cas d'erreur. On reproduit ce comportement via fetchRest sur l'endpoint storefront.
const useMaintenance = (): boolean | null => {
  const router = useRouter()
  const locale = (router.query.locale as string) || 'fr'
  const [isInMaintenance, setIsInMaintenance] = useState<boolean | null>(null)
  const cancelledRef = useRef(false)

  useEffect(() => {
    cancelledRef.current = false

    const checkOnce = async (): Promise<boolean> => {
      try {
        await fetchRest({
          endpoint: ApiQueryEndpoint.STOREFRONT.GET_SOCIETY_INFO.endpoint,
          locale,
        })
        return false
      } catch {
        return true
      }
    }

    const confirm = async (): Promise<boolean> => {
      for (let i = 0; i < CONFIRMATION_ATTEMPTS; i++) {
        const failed = await checkOnce()
        if (!failed) return false
        if (cancelledRef.current) return false
        if (i < CONFIRMATION_ATTEMPTS - 1) {
          await new Promise(r => setTimeout(r, CONFIRMATION_INTERVAL_MS))
        }
      }
      return true
    }

    const loop = async () => {
      while (!cancelledRef.current) {
        const failed = await checkOnce()
        if (cancelledRef.current) return

        if (failed) {
          const confirmed = await confirm()
          if (cancelledRef.current) return
          setIsInMaintenance(confirmed)
        } else {
          setIsInMaintenance(false)
        }

        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS))
      }
    }

    loop()

    return () => {
      cancelledRef.current = true
    }
  }, [locale])

  return isInMaintenance
}

export default useMaintenance
